import styled from "styled-components";
import DataSelectionModel from "./DataSelectionModal";
import * as yup from "yup";
import Form from "react-bootstrap/Form";
import { WfsGetFeatureRequest } from "../../types/Wfs/WfsGetFeatureRequest";
import { useDataSelectionContext } from "./DataSelectionContext";
import { useEffect, useMemo, useState } from "react";
import { useLayerService } from "../../hooks";
import { Option } from "../../types/Option";
import { Field } from "formik";

const LabelFixedWidth = styled.label`
  width: 200px;
`;

interface PublicTransportFormValues {
  importQuarter: string;
  allModes: boolean;
  modes: string[];
  outputFormat: string;
}

const modes: Option[] = [
  { id: 0, label: "Tram/Streetcar/Light rail" },
  { id: 1, label: "Subway/Metro" },
  { id: 2, label: "Rail" },
  { id: 3, label: "Bus" },
  { id: 4, label: "Ferry" },
  { id: 5, label: "Cable/Tram" },
  { id: 6, label: "Aerial Lift" },
  { id: 7, label: "Funicular" },
  { id: 11, label: "Trolleybus" },
  { id: 12, label: "Monorail" },
  { id: 200, label: "Coach" },
];

const PublicTransport = () => {
  const layerName = "basemap:publictransport";
  const { layers, getLayer, wfsOrder } = useDataSelectionContext();
  const layer = useMemo(() => getLayer(layerName), [layers]);
  const layerService = useLayerService();
  const [importQuarters, setImportQuarters] = useState<string[]>([]);
  const initialValues = useMemo(
    () =>
      ({
        outputFormat: "gtfs",
        allModes: true,
        modes: [],
        importQuarter: !!importQuarters.length
          ? importQuarters[importQuarters.length - 1]
          : "",
      } as PublicTransportFormValues),
    [layer, importQuarters]
  );
  const schema = useMemo(
    () =>
      yup.object().shape({
        importQuarter: yup.string().required(),
        allModes: yup.boolean().required(),
        modes: yup.array().when("allModes", {
          is: false,
          then: (schema) => schema.min(1).required(),
        }),
        outputFormat: yup
          .string()
          .oneOf(["gtfs", "txc", "cif", "gml"])
          .required(),
      }),
    [layer]
  );

  useEffect(() => {
    layerService
      .GetPublicTransportImportQuarters()
      .then((importQuarters) => setImportQuarters(importQuarters))
      .catch();
  }, []);

  const onSubmit = (values: PublicTransportFormValues) => {
    let filter = `ImportQuarter = "${values.importQuarter}"`;
    if (!values.allModes && values.modes.length !== modes.length) {
      filter += ` AND RouteType IN ${values.modes.join(",")}`;
    }

    wfsOrder({
      service: "WFS",
      version: "2.0.0",
      request: "GetFeature",
      resultType: "Results",
      typeNames: layerName,
      outputFormat: values.outputFormat,
      filter: filter,
    } as WfsGetFeatureRequest);
  };

  return (
    <>
      {!!importQuarters.length && (
        <DataSelectionModel
          headerText="Public Transport TimeTables"
          layer={layer}
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={onSubmit}
        >
          {({ values, handleChange, handleBlur, setFieldValue }) => (
            <>
              <Form.Group>
                <Form.Label htmlFor="importQuarter">Quarter</Form.Label>
                <Form.Select
                  id="importQuarter"
                  name="importQuarter"
                  value={values.importQuarter}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {importQuarters.map((importQuarter) => (
                    <option value={importQuarter}>{importQuarter}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label htmlFor="allModes">Modes</Form.Label>
                <Form.Check
                  id="allModes"
                  name="allModes"
                  type="switch"
                  checked={values.allModes}
                  label={values.allModes ? "All Modes" : "Selected Modes"}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.checked) {
                      setFieldValue("modes", []);
                    }
                  }}
                  onBlur={handleBlur}
                />
                {!values.allModes && (
                  <div
                    role="group"
                    className="d-flex flex-wrap justify-content-between"
                  >
                    {modes.map((mode) => (
                      <LabelFixedWidth>
                        <Field
                          type="checkbox"
                          name="modes"
                          value={mode.id.toString()}
                        />{" "}
                        {mode.label}
                      </LabelFixedWidth>
                    ))}
                  </div>
                )}
              </Form.Group>

              <Form.Group>
                <Form.Label htmlFor="outputFormat">Format</Form.Label>
                <Form.Select
                  id="outputFormat"
                  name="outputFormat"
                  value={values.outputFormat}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="gml">GML 3</option>
                  <option value="gtfs">GTFS</option>
                  <option value="txc">TransXchange 2.1</option>
                  <option value="cif">CIF</option>
                </Form.Select>
              </Form.Group>
            </>
          )}
        </DataSelectionModel>
      )}
    </>
  );
};

export default PublicTransport;
